<template>
    <div :id="componentId"
         :ref="componentId"
         class="baseComponent"
         v-bind:class="baseClassObject"
         v-tooltip="{content: errorMessage, placement: 'top'}"
         @click="$refs.input.focus()">
        <label class="baseInput inlineFont">
            <span>
                <slot></slot>
            </span>
            <input :type="inputType"
                   :class="{'disableDrag': focus}"
                   name="input"
                   ref="input"
                   :placeholder="getPlaceholder"
                   :disabled="isDisabled"
                   @keyup.esc="onEsc"
                   @keyup.enter="$event.target.blur()"
                   @focus="focus = true"
                   @blur="onBlur"
                   @input="inputChange"
                   onfocus="this.select()"
                   v-on:keypress="onKeyPress"
                   v-model="valueEdit">
            <tooltip :text="overrideTooltipText" v-if="valueOverridden && !focus"
                     class="override" :delay="500"
                     :style="overrideStyle">
                <span>*</span>
            </tooltip>
        </label>
        <button type="button" v-if="focus || confirmationHighlight"
                class="icon-button buttonInline disableDrag"
                v-on:click.stop="onBlur()">
            <IconBase width="13" height="10" :icon-color="confirmationIconColor"
                      :icon-hover-color="confirmationIconHoverColor"
                      icon-name="checkV2Icon"/>
        </button>
    </div>
</template>

<script>
import consts from 'services/metaData/consts';
import MessagesHelper from 'services/validators/messagesHelper';
import Vue from 'vue';
import tooltip from 'services/libWrappers/vue-tooltip';
import vuelidateWrap from 'services/libWrappers/vuelidate/vuelidate';
import IconBase from 'components/IconBase.vue';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import Helpers from './inlineInputHelpers';

tooltip(Vue);
vuelidateWrap(Vue);

export default {
    name: 'InlineInput',
    components: {
        Tooltip,
        IconBase
    },
    props: {
        value: {
            type: [String, Number],
            default: null
        },
        inputType: {
            type: String,
            default: 'text'
        },
        placeholder: {
            type: [String, Number]
        },
        attributeName: {
            type: String
        },
        attributeField: {
            type: String
        },
        uniqueId: {
            type: String
        },
        isFocus: {
            type: Boolean,
            default: false
        },
        validation: {
            type: Object
        },
        isDisabled: {
            type: Boolean,
            default: false
        },
        overrideTooltipText: {
            type: String,
            default: ''
        },
        valueOverridden: {
            type: Boolean,
            default: false
        },
        hidePlaceholderWithValueOnFocus: {
            type: String,
            default: null
        },
        typingPreventHelper: {
            type: String,
            default: null
        },
        confirmationHighlight: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            focus: false,
            originValue: null,
            errorMessage: '',
            valueEdit: null
        };
    },
    computed: {
        componentId() {
            return `ssaInlineInputId${this.uniqueId}`;
        },
        baseClassObject() {
            return {
                borderSuccess:
                    this.focus && (!this.validation || (this.validation && !this.validation.$invalid)),
                borderFailure: this.validation && this.validation.$invalid
            };
        },
        overrideStyle() {
            const value = this.value || this.placeholder;
            const valueString = value ? value.toString() : '';
            return {
                left: `${((valueString.length * 5) + 15)}px`
            };
        },
        getPlaceholder() {
            if (this.hidePlaceholderWithValueOnFocus && this.focus
                && this.placeholder === this.hidePlaceholderWithValueOnFocus) {
                return '';
            }
            if (this.focus && this.placeholder !== this.hidePlaceholderWithValueOnFocus && this.valueOverridden) {
                return `${this.placeholder} *`;
            }
            return this.placeholder;
        },
        confirmationIconHoverColor() {
            if (this.validation && this.validation.$invalid) {
                return consts.COLORS.LIGHT_700;
            }
            return consts.COLORS.PRIMARY_500;
        },
        confirmationIconColor() {
            if (this.confirmationHighlight) {
                return consts.COLORS.PRIMARY_500;
            }
            return consts.COLORS.LIGHT_700;
        }
    },
    watch: {
        value: {
            handler() {
                if (this.value !== this.valueEdit) {
                    this.originValue = this.value;
                    this.valueEdit = this.value;
                }
            }
        }
    },
    mounted() {
        this.focus = this.isFocus;
    },
    created() {
        this.originValue = this.value;
        this.valueEdit = this.value;
    },
    methods: {
        onKeyPress(event) {
            if (this.typingPreventHelper) {
                Helpers[this.typingPreventHelper](event);
            }
        },
        onEsc(event) {
            this.valueEdit = this.originValue;
            event.target.blur();
            this.inputChange();
        },
        onBlur(event) {
            this.focus = false;
            if (this.valueEdit === this.originValue) return;

            if (!this.validation || (this.validation && !this.validation.$invalid)) {
                this.originValue = this.valueEdit;
                this.$emit('changed-value', this.valueEdit);
            } else {
                this.onEsc(event);
            }
        },
        inputChange() {
            this.$emit('input', this.valueEdit);
            this.validationError();
        },
        validationError() {
            if (this.validation
                && this.validation.$invalid
                && this.attributeName
                && this.attributeField) {
                const errorMessages = MessagesHelper.getErrorMessages(
                    this.attributeName,
                    this.attributeField,
                    this.validation
                );
                this.errorMessage = errorMessages.length > 0 ? errorMessages.toString() : '';
            } else {
                this.errorMessage = '';
            }
        }
    }

};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";
    @import "~styles/buttons";
    @import '../../Tooltip/tooltip.css';

    $inputHeight: 32px;

    .inlineFont {
        @include font(6);
        color: $dark-300;

        input {
            @include font(6);

            &::placeholder, &::-webkit-input-placeholder, &:-ms-input-placeholder {
                @include font(7);
                color: $light-800;
            }
        }
    }

    .buttonInline {
        &.icon-button {
            padding: 0;
            height: auto;
            @include transparentButton;
        }
    }

    .baseInput {
        position: relative;
        display: grid;
        grid-template-columns: auto 1fr;
        grid-gap: 4px;
        line-height: $inputHeight;

        & input {
            padding: 0;
            border: none;
            background-color: transparent;
            width: 100%;

            &:focus {
                outline: 0;
            }

            line-height: $inputHeight;
        }
    }

    .baseComponent:hover {
        background-color: $light-300;
    }

    .baseComponent {
        cursor: text;
        display: grid;
        grid-gap: 2px;
        grid-template-columns: 1fr 25px;
        height: $inputHeight;
        padding-left: 4px;

        border-radius: 4px 4px 4px 4px;
        border-bottom: 2px solid transparent;

        $borderIndicationRadius: 4px 4px 0 0;
        $borderIndication: 2px solid;

        &.borderSuccess {
            border-radius: $borderIndicationRadius;
            border-bottom: $borderIndication $primary-500;
        }

        &.borderFailure {
            border-radius: $borderIndicationRadius;
            border-bottom: $borderIndication $negative-500;
        }
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    input[type=number] {
        -moz-appearance: textfield;
    }

    .override {
        position: absolute;
        display: block;
        top: -3px;
    }
</style>
